import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useSport } from "@/context/SportContext";
import { useAuth } from "@/context/AuthContext";
import SportSwitcher from "@/components/SportSwitcher";
import { Users, CalendarDays, PoundSterling, ArrowRight,
  CreditCard, ShieldCheck, BarChart3, ClipboardList, ShieldAlert, CalendarClock, MessageSquareWarning,
  Package, Link2, Sparkles, CheckSquare } from "lucide-react";
import { toast } from "sonner";
import PendingAssessmentsCard from "@/components/PendingAssessmentsCard";
import CredentialExpiryBanner from "@/components/CredentialExpiryBanner";
import AssessmentProgressWidget from "@/components/AssessmentProgressWidget";
import { PendingClaimsWidget } from "@/components/AssessmentClaimsWidgets";
import ScanSheetLauncher from "@/components/ScanSheetLauncher";
import RescheduleCutoffSetting from "@/components/RescheduleCutoffSetting";
import CoachSimpleLauncher from "@/components/CoachSimpleLauncher";
import { AttentionList } from "@/components/AttentionList";
import { PlayerInterestFeed } from "@/components/PlayerInterestFeed";

export default function CoachDashboard() {
  const { sport, meta } = useSport();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [nudge, setNudge] = useState(null);
  const [awaitingFeedback, setAwaitingFeedback] = useState([]);
  const [feedbackOwed, setFeedbackOwed] = useState(null);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  // Iter121 — session-local override so the coach can temporarily jump into
  // the full dashboard without flipping their saved preference off.
  const [forceFull, setForceFull] = useState(false);

  // Iter121 — Simplified Mode is opt-in. Legacy coaches without the field
  // default to the classic full console; new coaches will get true written
  // on first save from their profile.
  const simplifiedOn = !forceFull && user?.simplified_mode === true;

  const exitSimplified = () => {
    // Iter121 — Session-only escape hatch. The tile launcher's "Switch to
    // Full Dashboard" button flips this component's `forceFull` state, which
    // renders the full console for the rest of the session. The saved
    // preference is not mutated here so the back-button remains visible and
    // the launcher returns on next visit. Permanent OFF is done via
    // Profile → Simplified Mode.
    setForceFull(true);
  };

  useEffect(() => {
    api.get(`/dashboard/coach?sport=${sport}`).then((r) => setData(r.data)).catch(() => setData({}));
  }, [sport]);

  useEffect(() => {
    api.get("/coach/availability-nudge").then((r) => setNudge(r.data)).catch(() => setNudge(null));
  }, []);

  // "N players awaiting feedback" nudge — past assessment bookings without a
  // Mark-attended tick. Coach may have hit Reset which flipped these into
  // `awaiting_feedback` state; we surface them so nobody sits there for weeks.
  useEffect(() => {
    api.get("/coach/assessments?status_filter=awaiting_feedback&mine=1")
      .then((r) => setAwaitingFeedback(r.data || []))
      .catch(() => setAwaitingFeedback([]));
    api.get("/me/coach/pending-feedback").then((r) => setFeedbackOwed(r.data?.count ?? 0)).catch(() => setFeedbackOwed(null));
  }, []);

  // Compute perms up-front (before the loading return) so the pending-approvals
  // useEffect below can depend on `canManageUsers` without violating hook rules.
  const perms = new Set(user?.permissions || []);
  if (user?.role === "admin") perms.add("admin");
  const isSuperAdmin = perms.has("super_admin");
  const isAdmin = isSuperAdmin || perms.has("admin");
  const canManageUsers = isSuperAdmin || perms.has("manage_users");

  // Pending player approvals — admins only. Reuses the same queue endpoint
  // AdminOverview reads so we don't add a second listing endpoint. Poll every
  // 60s so newly approved players clear the pill without a hard refresh.
  useEffect(() => {
    if (!canManageUsers) return;
    let cancelled = false;
    const fetchPending = () => {
      api.get("/admin/players/pending")
        .then((r) => { if (!cancelled) setPendingApprovals(Array.isArray(r.data) ? r.data : []); })
        .catch(() => { if (!cancelled) setPendingApprovals([]); });
    };
    fetchPending();
    const t = setInterval(fetchPending, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [canManageUsers]);

  if (!data) return <div className="label-mono text-[#A3A3A3]">Loading dashboard…</div>;

  // Iter121 — Simplified Mode short-circuits the whole dashboard for a
  // large-tile launcher. Placed AFTER every hook + fetch so the switch-back
  // path is instant with no reload flash.
  if (simplifiedOn) {
    return (
      <div className="space-y-2">
        <div className="max-w-[1032px] mx-auto px-0 sm:px-0 space-y-6"><AttentionList /><PlayerInterestFeed /></div>
        <CoachSimpleLauncher onExitSimplified={exitSimplified} />
      </div>
    );
  }

  const adminShortcuts = isAdmin ? [
    { to: "/admin", label: "Admin console", desc: "All tools in one place", icon: ShieldCheck, color: "#CCFF00" },
    { to: "/admin/stats", label: "Stats & KPIs", desc: "Users · bookings · £", icon: BarChart3, color: "#CCFF00" },
    { to: "/admin/stripe", label: "Stripe config", desc: "API keys · audit-logged", icon: CreditCard, color: "#FF4500" },
    ...(isSuperAdmin ? [{ to: "/admin/users", label: "Users & roles", desc: "Edit any user's permissions", icon: Users, color: "#CCFF00" }] : []),
    { to: "/admin/applications", label: "Coach applications", desc: "Approve / reject pending", icon: ShieldAlert, color: "#FF4500" },
    { to: "/settings/pricing", label: "Pricing", desc: "Per-tier rates", icon: ClipboardList, color: "#CCFF00" },
  ] : [];

  const publicBaseUrl = typeof window !== "undefined" ? window.location.origin : "";
  const copyOfferingLink = async (offeringId) => {
    const url = `${publicBaseUrl}/offerings/${offeringId}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — share with your community");
    } catch { toast.error("Copy failed — long-press the link instead"); }
  };

  return (
    <div data-testid="coach-dashboard" className="space-y-8">
      <AttentionList />
      <PlayerInterestFeed />
      <div className="flex items-end justify-between flex-wrap gap-4 fade-up">
        <div>
          <div className="label-mono mb-2">COACH CONSOLE · {meta.label.toUpperCase()}</div>
          <h1 className="h-display text-5xl tracking-tighter">Today&apos;s session plan.</h1>
        </div>
        <div className="flex items-center gap-3">
          <SportSwitcher />
          {user?.simplified_mode === true && (
            <button
              type="button"
              onClick={() => setForceFull(false)}
              data-testid="coach-back-to-simplified-btn"
              className="text-xs uppercase tracking-wider border border-[#262626] hover:border-[#CCFF00] hover:text-[#CCFF00] text-[#A3A3A3] px-4 py-3 inline-flex items-center gap-2"
            >
              ← Simplified Mode
            </button>
          )}
          <Link to="/players" className="btn-volt px-5 py-3 text-xs inline-flex items-center gap-2">
            Players <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>

      {/* Credential expiry banner — shown when own cert/insurance is within 30 days */}
      <CredentialExpiryBanner />

      {/* Feedback nudge — surfaces past assessment bookings that still don't
          have Mark-attended ticked. Coach hit Reset OR forgot to tick after
          the session. Links to Players so they can jump straight in. */}
      {awaitingFeedback.length > 0 && (
        <Link
          to="/players"
          data-testid="awaiting-feedback-pill"
          className="card-surface p-3 sm:p-4 border-l-4 border-[#FF4500] fade-up d1 flex items-center gap-3 hover:bg-[#141414] transition-colors"
        >
          <MessageSquareWarning className="w-5 h-5 text-[#FF4500] shrink-0" strokeWidth={1.5} />
          <div className="flex-1 min-w-0">
            <div className="label-mono text-[#FF4500]">AWAITING FEEDBACK · {awaitingFeedback.length}</div>
            <div className="text-xs sm:text-sm text-white mt-0.5">
              {awaitingFeedback.length === 1 ? "1 player is" : `${awaitingFeedback.length} players are`} waiting on your session note. Tap to open the roster.
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-[#A3A3A3] shrink-0" />
        </Link>
      )}

      {/* Pending player approvals — admins only. Bright volt-yellow pill so
          new signups don't sit for days. Deep-links to the queue widget on
          /admin (anchor #pending-players). Mirrors the AWAITING FEEDBACK pill. */}
      {canManageUsers && pendingApprovals.length > 0 && (
        <Link
          to="/admin#pending-players"
          data-testid="pending-approvals-pill"
          className="card-surface p-3 sm:p-4 border-l-4 border-[#CCFF00] fade-up d1 flex items-center gap-3 hover:bg-[#141414] transition-colors"
        >
          <ShieldCheck className="w-5 h-5 text-[#CCFF00] shrink-0" strokeWidth={1.5} />
          <div className="flex-1 min-w-0">
            <div className="label-mono text-[#CCFF00]">PENDING APPROVAL · {pendingApprovals.length}</div>
            <div className="text-xs sm:text-sm text-white mt-0.5">
              {pendingApprovals.length === 1 ? "1 player is" : `${pendingApprovals.length} players are`} awaiting review. Tap to open the approval queue.
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-[#A3A3A3] shrink-0" />
        </Link>
      )}

      {/* Availability nudge — surfaces when the coach has students on queued
          session plans but nothing bookable in the next 7 days. */}
      {nudge?.nudge && (
        <div
          className="card-surface p-4 sm:p-5 border-l-4 border-[#CCFF00] fade-up d1 flex items-center gap-4 flex-wrap"
          data-testid="availability-nudge"
          role="alert"
        >
          <CalendarClock className="w-6 h-6 text-[#CCFF00]" strokeWidth={1.5} />
          <div className="flex-1 min-w-0">
            <div className="label-mono text-[#CCFF00]">HEADS UP</div>
            <div className="text-sm text-white mt-1">{nudge.message}</div>
          </div>
          <Link
            to="/availability"
            className="btn-volt py-2 px-4 text-xs inline-flex items-center gap-2 shrink-0"
            data-testid="availability-nudge-cta"
          >
            Publish a window <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}

      {/* Pending assessment claims — students claiming a prior paid assessment with this coach */}
      <PendingClaimsWidget />

      {/* Quick-launch tiles — currently just Scan Sheet; grid ready for more */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 fade-up d1">
        <ScanSheetLauncher />
      </div>

      {/* Global reschedule/cancel cutoff */}
      <RescheduleCutoffSetting className="fade-up d1" />

      {/* Admin shortcuts — only for admin/super_admin */}
      {adminShortcuts.length > 0 && (
        <div className="card-surface p-4 sm:p-5 border-l-4 border-[#CCFF00] fade-up d1" data-testid="admin-shortcuts">
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <div className="label-mono inline-flex items-center gap-2" style={{ color: "#CCFF00" }}>
              <ShieldCheck className="w-3.5 h-3.5" /> ADMIN QUICK LINKS
            </div>
            <Link to="/admin" className="text-xs text-[#A3A3A3] hover:text-[#CCFF00] uppercase tracking-wider inline-flex items-center gap-1" data-testid="admin-full-console-link">
              Full console <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            {adminShortcuts.map((t) => (
              <Link
                key={t.to}
                to={t.to}
                data-testid={`admin-shortcut-${t.to.replace(/[/]/g, "-").slice(1)}`}
                className="p-3 border border-[#262626] hover:border-[#CCFF00] transition-all group"
              >
                <t.icon strokeWidth={1.75} className="w-4 h-4 mb-2" style={{ color: t.color }} />
                <div className="text-xs font-bold uppercase tracking-wider leading-tight group-hover:text-[#CCFF00] transition-colors">{t.label}</div>
                <div className="text-[10px] text-[#525252] mt-1 leading-tight">{t.desc}</div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Pending assessments — coach marks players attended in one click */}
      <PendingAssessmentsCard />

      <AssessmentProgressWidget />

      {/* KPIs */}
      <div className="grid md:grid-cols-3 gap-4 fade-up d1">
        <Kpi icon={Users} label="Active players" value={data.students_count} color={meta.color} />
        <Kpi icon={CalendarDays} label="Upcoming sessions" value={data.upcoming_count} color="#007AFF" />
        <RevenueKpi breakdown={data.monthly_revenue_breakdown} fallback={data.monthly_revenue} />
      </div>

      {/* Iter60: This Week's Diary + To-Do — replaces the roster radar + focus
          areas block, which duplicated per-player insights already visible via
          Players. Merges published offerings (incl. those with 0 bookings so
          the coach can share the link) and coach's own 1:1 bookings. */}
      <ThisWeekDiary
        offerings={data.week_offerings || []}
        bookings={data.week_bookings || []}
        onShareLink={copyOfferingLink}
        todo={data.todo || {}}
        feedbackOwedCount={feedbackOwed}
      />
    </div>
  );
}

// ---- This Week's Diary (iter60) --------------------------------------------
// Left col: chronological session strip grouped by day. Right col: To-Do.
function ThisWeekDiary({ offerings, bookings, onShareLink, todo, feedbackOwedCount }) {
  const items = useMemo(() => {
    const merged = [
      ...offerings.map((o) => ({ ...o, _sort: o.start })),
      ...bookings.map((b) => ({ ...b, _sort: b.start })),
    ].sort((a, b) => (a._sort || "").localeCompare(b._sort || ""));
    // Group by yyyy-mm-dd for the day-labelled sections
    const groups = new Map();
    for (const it of merged) {
      const key = (it.start || "").slice(0, 10);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(it);
    }
    return Array.from(groups.entries()).map(([day, rows]) => ({ day, rows }));
  }, [offerings, bookings]);

  const feedbackCount = feedbackOwedCount ?? todo.feedback_owed ?? 0;

  return (
    <div className="grid lg:grid-cols-3 gap-4 fade-up d2" data-testid="this-week-diary">
      {/* Diary — 2 cols */}
      <div className="card-surface p-6 lg:col-span-2">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="label-mono inline-flex items-center gap-2">
            <CalendarClock className="w-3.5 h-3.5 text-[#CCFF00]" /> THIS WEEK · NEXT 7 DAYS
          </div>
          <Link to="/offerings" className="text-xs uppercase tracking-wider text-[#CCFF00] hover:underline">
            All offerings <ArrowRight className="w-3 h-3 inline" />
          </Link>
        </div>
        {items.length === 0 ? (
          <div className="text-sm text-[#A3A3A3]" data-testid="diary-empty">
            Nothing on the diary this week. <Link to="/offerings/new" className="text-[#CCFF00] underline">Publish an offering</Link> to get bookings rolling.
          </div>
        ) : (
          <div className="space-y-4">
            {items.map(({ day, rows }) => (
              <DiaryDay key={day} day={day} rows={rows} onShareLink={onShareLink} />
            ))}
          </div>
        )}
      </div>

      {/* To-Do — 1 col */}
      <div className="card-surface p-6" data-testid="coach-todo">
        <div className="label-mono mb-4 inline-flex items-center gap-2">
          <CheckSquare className="w-3.5 h-3.5 text-[#FF4500]" /> TO-DO
        </div>
        <div className="space-y-2">
          <TodoRow
            testid="todo-feedback-owed"
            to="/coach/feedback"
            icon={MessageSquareWarning}
            iconColor="#FF4500"
            label="Feedback owed"
            count={feedbackCount}
            emptyText="You're all caught up — nice."
          />
          <TodoRow
            testid="todo-empty-sessions"
            to="/offerings"
            icon={Sparkles}
            iconColor="#FFD700"
            label="Empty sessions this week"
            count={todo.empty_offerings_this_week || 0}
            emptyText="Every published session has at least one booking."
          />
          <TodoRow
            testid="todo-unpaid-packs"
            to="/packages"
            icon={Package}
            iconColor="#66C2FF"
            label="Unpaid pack invites"
            count={todo.unpaid_packs || 0}
            emptyText="No outstanding pack payments."
          />
        </div>
      </div>
    </div>
  );
}

function DiaryDay({ day, rows, onShareLink }) {
  // Format: "Mon 5 Feb"
  const d = new Date(day);
  const label = d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
  return (
    <div>
      <div className="label-mono text-[#A3A3A3] mb-2">{label.toUpperCase()}</div>
      <div className="divide-y divide-[#262626] border-t border-[#262626]">
        {rows.map((r) => <DiaryRow key={`${r.kind || "row"}-${r.id}`} r={r} onShareLink={onShareLink} />)}
      </div>
    </div>
  );
}

function DiaryRow({ r, onShareLink }) {
  const t = new Date(r.start);
  const time = t.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  const isOffering = r.kind === "offering";
  const empty = isOffering && (r.bookings_count || 0) === 0;
  const capacity = isOffering ? `${r.bookings_count || 0}/${r.max_spaces || 1}` : "1:1";
  const accent = empty ? "#FFD700" : (r.session_type === "one_on_one" ? "#CCFF00" : r.session_type === "performance" ? "#FF4500" : "#66C2FF");
  return (
    <div className="py-3 flex items-center gap-3" data-testid={`diary-row-${r.id}`}>
      <div className="w-1 h-10 shrink-0" style={{ background: accent }} />
      <div className="w-14 shrink-0 label-mono text-[#A3A3A3]">{time}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold truncate">{r.title}</div>
        <div className="text-[10px] text-[#A3A3A3] mt-0.5">
          {(r.sport || "").toUpperCase()}{r.is_assessment ? " · ASSESSMENT" : ""}
        </div>
      </div>
      <div className="label-mono shrink-0" style={{ color: accent }}>
        {empty ? "EMPTY" : capacity}
      </div>
      {isOffering ? (
        empty ? (
          <button
            onClick={() => onShareLink(r.id)}
            data-testid={`diary-share-${r.id}`}
            className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 inline-flex items-center gap-1 shrink-0"
            style={{ background: "#FFD700", color: "#0A0A0A" }}
          >
            <Link2 className="w-3 h-3" /> Share
          </button>
        ) : (
          <Link
            to={`/offerings/${r.id}`}
            data-testid={`diary-open-${r.id}`}
            className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 inline-flex items-center gap-1 shrink-0 border border-[#262626] hover:border-[#CCFF00]"
          >
            Open <ArrowRight className="w-3 h-3" />
          </Link>
        )
      ) : (
        <Link
          to={r.student_id ? `/players/${r.student_id}` : "/calendar"}
          data-testid={`diary-player-${r.id}`}
          className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 inline-flex items-center gap-1 shrink-0 border border-[#262626] hover:border-[#CCFF00]"
        >
          Player <ArrowRight className="w-3 h-3" />
        </Link>
      )}
    </div>
  );
}

function TodoRow({ testid, to, icon: Icon, iconColor, label, count, emptyText }) {
  const isEmpty = !count;
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`flex items-center gap-3 p-3 border transition-colors ${isEmpty ? "border-[#1F1F1F] opacity-60" : "border-[#262626] hover:border-[#CCFF00]"}`}
    >
      <Icon strokeWidth={1.5} className="w-4 h-4 shrink-0" style={{ color: iconColor }} />
      <div className="flex-1 min-w-0">
        <div className="text-xs font-bold uppercase tracking-wider truncate">{label}</div>
        <div className="text-[10px] text-[#A3A3A3] truncate">{isEmpty ? emptyText : "Tap to review"}</div>
      </div>
      <div className="h-display text-xl tracking-tight shrink-0" style={{ color: isEmpty ? "#525252" : iconColor }}>
        {count}
      </div>
    </Link>
  );
}

function Kpi({ icon: Icon, label, value, color }) {
  return (
    <div className="card-surface p-6 relative overflow-hidden" data-testid={`kpi-${label.replace(/\s+/g,'-').toLowerCase()}`}>
      <div className="flex items-start justify-between mb-6">
        <div className="label-mono">{label}</div>
        <Icon strokeWidth={1.5} className="w-5 h-5" style={{ color }} />
      </div>
      <div className="h-display text-5xl tracking-tighter" style={{ color }}>{value ?? 0}</div>
    </div>
  );
}

// Iter99 — Revenue KPI with Personal / Team split. Falls back to the legacy
// `monthly_revenue` int if the backend hasn't shipped the breakdown yet
// (defensive during rolling deploys). "Team" here means revenue from
// offerings I've marked shared_with_team — this coach still counts them
// because they were sold under their listing.
function RevenueKpi({ breakdown, fallback }) {
  const total = breakdown ? breakdown.total : (fallback || 0);
  const personal = breakdown ? breakdown.personal : total;
  const team = breakdown ? breakdown.team : 0;
  return (
    <div className="card-surface p-6 relative overflow-hidden" data-testid="kpi-revenue-this-month">
      <div className="flex items-start justify-between mb-4">
        <div className="label-mono">Revenue this month</div>
        <PoundSterling strokeWidth={1.5} className="w-5 h-5" style={{ color: "#FF4500" }} />
      </div>
      <div className="h-display text-4xl tracking-tighter" style={{ color: "#FF4500" }} data-testid="kpi-revenue-total">
        £{total}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 pt-3 border-t border-[#262626]">
        <div>
          <div className="text-[9px] uppercase tracking-widest text-[#A3A3A3]">Personal</div>
          <div className="text-lg font-bold text-white" data-testid="kpi-revenue-personal">£{personal}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-widest text-[#A3A3A3]">Team</div>
          <div className="text-lg font-bold text-[#CCFF00]" data-testid="kpi-revenue-team">£{team}</div>
        </div>
      </div>
    </div>
  );
}
